import {getKeycloakUserManager, isDeadSessionError, renewKeycloakUser} from './keycloak'

/**
 * Bearer token for the API, usable outside React — the Apollo client is
 * instantiated outside the tree, so this cannot be a hook.
 *
 * The access token, never the ID token: the audience mapper puts ambler-api on
 * the access token only, and the API checks that audience.
 *
 * Resolves to undefined only when there is no live session; the request then
 * goes out anonymous. Any other failure is thrown — an identity-provider
 * outage must not look like a signed-out user, or every request would go out
 * anonymous and Apollo's errorLink would bounce the user to /logout.
 */
export const getBearerToken = async (): Promise<string | undefined> => {
  let user = await getKeycloakUserManager().getUser()

  if (!user || user.expired) {
    try {
      user = await renewKeycloakUser()
    } catch (err) {
      if (isDeadSessionError(err)) {
        return undefined
      }
      throw err
    }
  }

  return user?.access_token
}

/**
 * The subject of the live session, straight from the claims the library keeps
 * decoded — no hand-rolled JWT parsing, which is where base64url and UTF-8
 * bugs live.
 */
export const getSessionSubject = async (): Promise<string | undefined> => {
  const user = await getKeycloakUserManager().getUser()

  return user?.profile.sub
}
